/**
 * Centralized route constants for the Judge web application.
 * Eliminates hardcoded route strings scattered throughout the codebase.
 *
 * Usage:
 *   import { ROUTES } from '@/shared/constants/routes';
 *   navigate(ROUTES.PRODUCTS);
 *   navigate(ROUTES.PRODUCT_DETAIL('some-id'));
 */

export const ROUTES = {
  HOME: '/',
  // Products
  PRODUCTS: '/products',
  PRODUCT_DETAIL: (id: string) => `/products/${id}`,
  PRODUCT_SECURITY_PLAN: (id: string) => `/products/${id}?tab=overview`,
  PRODUCT_IMPACT_REPORTS: (id: string) => `/products/${id}/impact-reports`,
  IMPACT_REPORT: (productId: string, id: string) => `/products/${productId}/impact-reports/${id}`,
  PR_IMPACT: (repositoryId: string, prNumber: number) => `/repositories/${repositoryId}/pull-requests/${prNumber}/impact`,
  // Repositories

  // Test Plans (tenant-level catalog)
  TEST_PLANS: '/test-plans',
  TEST_PLAN_DETAIL: (id: string) => `/test-plans/${id}`,
  // Controls (policy-first: evidence is bound policies' verdicts)
  CONTROLS: '/controls',
  CONTROL_DETAIL: (catalogUuid: string, controlId: string) => `/controls/${encodeURIComponent(catalogUuid)}/${encodeURIComponent(controlId)}`,
  // Billing (Stripe one-time 33-day access)
  SIGNUP: '/signup',
  BILLING_EXPIRED: '/billing-expired',
  // Onboarding
  ONBOARDING: {
    BOOTSTRAP: '/onboarding/bootstrap',
    ORGANIZATION: '/onboarding/organization',
    GITHUB_APP: '/onboarding/github-app',
    PRODUCT: '/onboarding/product',
    VIP: (token: string) => `/onboarding/vip/${token}`,
    VIP_PRODUCT: '/onboarding/vip-product',
    GITHUB_CALLBACK: '/onboarding/github-callback'
  },
  // Settings
  SETTINGS: {
    ROOT: '/settings',
    API_TOKENS: '/settings/api-tokens',
    // Connectors is the top-level tenant-scoped surface for both
    // inbound Integrations (GitHub + Vanta) and outbound Webhooks.
    // /settings/webhooks now redirects to ?tab=webhooks here.
    CONNECTORS: '/settings/connectors',
    CONNECTORS_WITH_TAB: (tab: 'integrations' | 'webhooks') => `/settings/connectors?tab=${tab}`,
    WEBHOOKS: '/settings/webhooks',
    // Directory is the combined organizations + teams + people surface.
    // The legacy /settings/organizations, /settings/people-teams, and
    // /settings/teams routes all redirect into it (see routes.tsx).
    DIRECTORY: '/settings/directory',
    DIRECTORY_WITH_TAB: (tab: 'members' | 'teams' | 'organization') => `/settings/directory?tab=${tab}`,
    DIRECTORY_TEAM: (teamId: string) => `/settings/directory?tab=teams&team=${teamId}`,
    ADMIN: '/settings/admin',
    ADMIN_WITH_TAB: (tab: string) => `/settings/admin?tab=${tab}`,
    RESOURCES: '/settings/resources'
  }
} as const;

/**
 * Helper to extract an ID from a path segment.
 * Useful for parsing dynamic routes.
 *
 * @example
 * const id = extractIdFromPath('/products/abc-123/edit', '/products/');
 * // returns 'abc-123'
 */
export function extractIdFromPath(pathname: string, prefix: string): string | undefined {
  return pathname.split(prefix)[1]?.split('/')[0];
}